import {COMPILE_ORDER, VISUAL_DIRECTION_CATEGORIES} from './catalog.js';
import {optionById, slotLimit, slotOf, VISUAL_DIRECTION_LIBRARY_VERSION} from './library.js';

/* series sits above the film for an episode; generation is request-local and
   never stored as production state. Both are injected by the caller. */
const SCOPE_RANK = Object.freeze({series: -1, project: 0, sequence: 1, scene: 2, shot: 3, generation: 4});
const INJECTED_SCOPES = new Set(['series', 'generation']);
const list = value => Array.isArray(value) ? value : [];
const record = value => value && typeof value === 'object' && !Array.isArray(value) ? value : {};

export function projectScenes(project = {}) {
  return list(project.scenes);
}

export function projectShots(project = {}) {
  return list(project.shots);
}

export function shotSceneId(shot = {}) {
  return String(shot.sceneId || shot.scene || '');
}

export function sequenceIdForScene(project = {}, sceneId = '') {
  if (!sceneId) return '';
  const scene = projectScenes(project).find(item => item.id === sceneId);
  if (scene?.sequenceId) return String(scene.sequenceId);
  return String(list(project.sequencePlan).find(sequence => list(sequence.sceneIds).includes(sceneId))?.id || '');
}

export function isShotApproved(shot = {}) {
  return Boolean(shot.lockedCompositionId || shot.approvedCompositionId || shot.status === 'approved' || list(shot.takes).some(take => take && take.approved));
}

export function sceneTimeOfDay(scene = {}) {
  const heading = String(scene.slug || scene.heading || scene.title || '').toUpperCase();
  const time = heading.split(/\s[-–—]\s|\s-\s/).slice(1).join(' ') || heading;
  if (/\b(NIGHT|MIDNIGHT|LATE NIGHT)\b/.test(time)) return 'night';
  if (/\b(DUSK|SUNSET|EVENING|DAWN|SUNRISE|MAGIC HOUR|GOLDEN HOUR|TWILIGHT)\b/.test(time)) return 'dusk';
  if (/\b(DAY|MORNING|NOON|AFTERNOON|MIDDAY)\b/.test(time)) return 'day';
  return '';
}

export function targetFor(project = {}, request = {}) {
  const shotId = String(request.shotId || '');
  const shot = shotId ? projectShots(project).find(item => item.id === shotId) || null : null;
  const sceneId = String(request.sceneId || (shot ? shotSceneId(shot) : '') || '');
  const scene = sceneId ? projectScenes(project).find(item => item.id === sceneId) || null : null;
  const sequenceId = String(request.sequenceId || sequenceIdForScene(project, sceneId) || '');
  const scope = shot ? 'shot' : scene ? 'scene' : sequenceId ? 'sequence' : 'project';
  return {projectId: String(project.id || ''), scope, sequenceId, sceneId: scene ? sceneId : '', shotId: shot ? shotId : '', scene, shot};
}

export function entryAppliesTo(entry = {}, target = {}) {
  if (entry.scope === 'project' || INJECTED_SCOPES.has(entry.scope)) return true;
  if (entry.scope === 'sequence') return Boolean(target.sequenceId) && entry.sequenceId === target.sequenceId;
  if (entry.scope === 'scene') return Boolean(target.sceneId) && entry.sceneId === target.sceneId;
  if (entry.scope === 'shot') return Boolean(target.shotId) && entry.shotId === target.shotId;
  return false;
}

export function entrySlot(entry = {}) {
  if (entry.slot && entry.category) return `${entry.category}:${entry.slot}`;
  return slotOf(optionById(entry.optionId));
}

function describe(entry, option, targetScope) {
  return {
    entryId: entry.id,
    optionId: option.id,
    category: option.category,
    slot: entry.slot || option.slot || 'main',
    displayName: option.displayName,
    creativeEffect: option.creativeEffect || option.description || '',
    scope: entry.scope,
    inherited: entry.scope !== targetScope && entry.scope !== 'generation',
    ...(entry.seriesRevisionId ? {seriesRevisionId: entry.seriesRevisionId, seriesRevisionNumber: entry.seriesRevisionNumber || null} : {}),
    locked: entry.locked || false,
    lockFacts: entry.lockFacts || null,
    source: entry.source || 'user',
    presetId: entry.presetId || null,
    appliedAt: entry.appliedAt || null,
    appliedBy: entry.appliedBy || null,
    override: entry.override || null,
    pinned: entry.kind === 'pin'
  };
}

/* The most specific choice wins, except that a series lock holds for every
   episode below it, and a generation-local override never defeats any lock. */
function lockHolds(current, entry) {
  if (!current.entries.some(item => item.locked)) return false;
  if (entry.scope === 'generation') return true;
  return current.entries.some(item => item.locked && item.scope === 'series');
}

function resolveChain(entries, target, blocked = []) {
  const slots = new Map();
  const applicable = entries.filter(entry => entryAppliesTo(entry, target)).sort((a, b) => SCOPE_RANK[a.scope] - SCOPE_RANK[b.scope] || String(a.appliedAt || '').localeCompare(String(b.appliedAt || '')));
  for (const entry of applicable) {
    const slot = entrySlot(entry);
    if (!slot) continue;
    const current = slots.get(slot) || {entries: [], overridden: []};
    if (lockHolds(current, entry)) {
      blocked.push({entryId: entry.id, optionId: entry.optionId || null, scope: entry.scope, slot, lockedBy: current.entries.filter(item => item.locked).map(item => ({optionId: item.optionId, scope: item.scope}))});
      continue;
    }
    if (entry.kind === 'pin') {
      current.overridden.push(...current.entries);
      current.entries = (entry.pinnedIds || []).map(optionId => ({...entry, optionId}));
    } else if (slotLimit(slot) > 1) {
      current.entries = [...current.entries.filter(item => item.optionId !== entry.optionId), entry].slice(-slotLimit(slot));
    } else {
      current.overridden.push(...current.entries);
      current.entries = [entry];
    }
    slots.set(slot, current);
  }
  return slots;
}

function continuityLocksFor(project, state, target) {
  const locks = [];
  const entries = list(state?.entries);
  for (const entry of entries) {
    if (entry.locked !== 'continuity' || !entryAppliesTo(entry, target)) continue;
    locks.push({entryId: entry.id, optionId: entry.optionId, category: entry.category, facts: entry.lockFacts || {}, scope: entry.scope, source: 'visual-direction'});
  }
  if (target.shotId && target.sceneId) {
    const sceneShots = projectShots(project).filter(shot => shotSceneId(shot) === target.sceneId);
    const at = sceneShots.findIndex(shot => shot.id === target.shotId);
    const previous = at > 0 ? sceneShots[at - 1] : null;
    if (previous) {
      for (const entry of entries) {
        if (entry.locked !== 'continuity' || entry.scope !== 'shot' || entry.shotId !== previous.id) continue;
        locks.push({entryId: entry.id, optionId: entry.optionId, category: entry.category, facts: entry.lockFacts || {}, scope: 'shot', source: 'previous-shot', shotId: previous.id, shotLabel: String(previous.code || previous.id)});
      }
    }
  }
  const time = target.scene ? sceneTimeOfDay(target.scene) : '';
  if (time) locks.push({category: 'time', facts: {timeOfDay: time}, scope: 'scene', source: 'scene-heading'});
  return locks;
}

const TIME_OK = Object.freeze({day: ['day', 'dusk'], golden: ['day', 'dusk'], night: ['night', 'dusk']});

export function continuityConflictsFor(option, locks = [], {override = null} = {}) {
  if (!option) return [];
  const conflicts = [];
  const attributes = option.attributes || {};
  for (const lock of locks) {
    if (lock.optionId === option.id) continue;
    if (lock.category === 'time' && attributes.timeOfDay) {
      if (!(TIME_OK[attributes.timeOfDay] || []).includes(lock.facts.timeOfDay)) {
        conflicts.push({type: 'continuity', kind: 'time-of-day', severity: 'blocker', optionId: option.id, lock, overridden: Boolean(override?.reason), message: `The scene heading sets ${lock.facts.timeOfDay}; ${option.displayName} reads as ${attributes.timeOfDay === 'golden' ? 'golden hour' : attributes.timeOfDay}.`});
      }
    }
    if (lock.category === 'lighting' && option.category === 'lighting' && lock.facts.keySide && ['back', 'under', 'top'].includes(attributes.keySide)) {
      const where = lock.source === 'previous-shot' ? `The previous shot (${lock.shotLabel}) establishes` : 'A continuity lock holds';
      conflicts.push({type: 'continuity', kind: 'light-direction', severity: 'blocker', optionId: option.id, lock, overridden: Boolean(override?.reason), message: `${where} the key light from ${lock.facts.keySide.replace('-', ' ')}. ${option.displayName} moves the key ${attributes.keySide === 'back' ? 'behind the subject' : attributes.keySide === 'under' ? 'below the face' : 'overhead'} and may reverse the direction.`});
    }
  }
  return conflicts;
}

function creativeConflicts(resolved, acknowledgements, targetKey) {
  const conflicts = [];
  const items = resolved.flatMap(slot => slot.items);
  for (let i = 0; i < items.length; i += 1) {
    for (let j = i + 1; j < items.length; j += 1) {
      const a = optionById(items[i].optionId);
      const b = optionById(items[j].optionId);
      if (!a || !b || !a.conflictsWith.includes(b.id)) continue;
      const pair = [a.id, b.id].sort().join('|');
      const swappable = [items[i], items[j]].filter(item => optionById(optionById(item.optionId)?.substitute));
      const replace = swappable.length > 1 ? swappable.find(item => item.inherited) || swappable[1] : swappable[0] || null;
      const replaceOption = replace ? optionById(replace.optionId) : null;
      const substitute = replaceOption ? optionById(replaceOption.substitute) : null;
      conflicts.push({
        type: 'creative',
        severity: 'warning',
        pair,
        optionIds: [a.id, b.id],
        acknowledged: acknowledgements.some(item => item.pair === pair && (item.target === targetKey || item.target === 'project')),
        message: `${a.displayName} + ${b.displayName} pull the image in different directions.`,
        suggestion: substitute ? {replace: replaceOption.id, with: substitute.id, entryId: replace.entryId, scope: replace.scope, label: `Switch ${replaceOption.displayName} → ${substitute.displayName}`} : null
      });
    }
  }
  return conflicts;
}

export function targetKeyOf(target = {}) {
  return target.shotId ? `shot:${target.shotId}` : target.sceneId ? `scene:${target.sceneId}` : target.sequenceId ? `sequence:${target.sequenceId}` : 'project';
}

export function resolveEffectiveVisualDirection(project = {}, request = {}) {
  const state = project.visualDirection || {};
  const target = targetFor(project, request);
  const style = {...record(project.filmDirection?.styleLock), ...record(project.visualStyleLock), ...record(target.scene?.visualStyleLock), ...record(target.shot?.shotSpec?.style), ...record(target.shot?.style)};
  const renderingStyle = Object.fromEntries(['medium', 'realism', 'texture', 'preset', 'label'].filter(key => style[key]).map(key => [key, style[key]]));
  const entries = list(state.entries).filter(entry => entry && (entry.kind === 'pin' || optionById(entry.optionId)));
  const blockedByLock = [];
  const slots = resolveChain(entries, target, blockedByLock);
  const resolved = [];
  for (const [slot, value] of slots) {
    const items = value.entries.map(entry => {
      const option = optionById(entry.optionId);
      return option ? {...describe(entry, option, target.scope), overrides: [...new Set(value.overridden.map(item => item.scope))].filter(scope => scope !== entry.scope)} : null;
    }).filter(Boolean);
    if (items.length) resolved.push({slot, category: slot.split(':')[0], items});
  }
  resolved.sort((a, b) => COMPILE_ORDER.indexOf(a.category) - COMPILE_ORDER.indexOf(b.category) || a.slot.localeCompare(b.slot));
  const layers = {series: [], project: [], sequence: [], scene: [], shot: [], generation: []};
  for (const entry of entries) if (entryAppliesTo(entry, target) && layers[entry.scope]) layers[entry.scope].push(entry);
  const resolvedByCategory = Object.fromEntries(VISUAL_DIRECTION_CATEGORIES.map(category => [category.id, resolved.filter(slot => slot.category === category.id).flatMap(slot => slot.items)]));
  const optionIds = Object.fromEntries(Object.entries(resolvedByCategory).map(([category, items]) => [category, items.map(item => item.optionId)]));
  const continuityLocks = continuityLocksFor(project, state, target);
  const continuity = resolved.flatMap(slot => slot.items).flatMap(item => continuityConflictsFor(optionById(item.optionId), continuityLocks.filter(lock => lock.entryId !== item.entryId), {override: item.override}));
  const targetKey = targetKeyOf(target);
  const creative = creativeConflicts(resolved, list(state.acknowledgements), targetKey);
  const framing = resolvedByCategory.framing.map(item => optionById(item.optionId));
  const lens = resolvedByCategory.lens.map(item => optionById(item.optionId)).find(option => option?.attributes?.focalMm);
  const composition = framing.length || lens ? {
    framingId: framing.find(option => option.slot === 'composition')?.id || framing[0]?.id || null,
    shotSizeId: framing.find(option => option.slot === 'size')?.id || null,
    shotSize: framing.map(option => option.attributes?.shotSize).find(Boolean) || null,
    compositionType: framing.map(option => option.attributes?.compositionType).find(Boolean) || null,
    foregroundLayer: framing.some(option => option.attributes?.foregroundLayer),
    angle: framing.map(option => option.attributes?.angle).find(Boolean) || null,
    focalMm: lens?.attributes?.focalMm || null
  } : null;
  const editRelationships = resolvedByCategory.technique.map(item => optionById(item.optionId)).filter(option => option?.attributes?.editRelationship).map(option => ({optionId: option.id, relationship: option.attributes.editRelationship, shotId: target.shotId || null}));
  return {
    libraryVersion: state.libraryVersion || VISUAL_DIRECTION_LIBRARY_VERSION,
    renderingStyle,
    target: {projectId: target.projectId, scope: target.scope, sequenceId: target.sequenceId || null, sceneId: target.sceneId || null, shotId: target.shotId || null},
    targetKey,
    layers,
    resolved,
    resolvedByCategory,
    optionIds,
    continuityLocks,
    conflicts: [...continuity, ...creative],
    blockers: continuity.filter(conflict => !conflict.overridden),
    blockedByLock,
    composition,
    editRelationships
  };
}
